import React, { useSyncExternalStore } from 'react';

export type NotificationType = 'default' | 'success' | 'error' | 'warning' | 'info' | 'library' | 'sparkles';

export interface Notification {
    id: string;
    message: string;
    description?: string;
    type: NotificationType;
    isVisible: boolean;
    link?: string;
    action?: (() => void) | null;
    actionLabel?: string;
}

export interface ToastOptions {
    description?: string;
    duration?: number;
    link?: string;
    action?: () => void;
    actionLabel?: string;
}

let notification: Notification = {
    id: '',
    message: '',
    description: '',
    type: 'default',
    isVisible: false,
    link: undefined,
    action: null,
    actionLabel: undefined,
};

const listeners = new Set<() => void>();

const emitChange = () => {
    listeners.forEach((listener) => listener());
};

let dismissTimer: ReturnType<typeof setTimeout> | null = null;

export function notify(
    message: string,
    type: NotificationType = 'default',
    description?: string,
    duration: number = 3000,
    link?: string,
    action?: (() => void) | null,
    actionLabel?: string,
): void {
    if (dismissTimer) clearTimeout(dismissTimer);

    // Force everything to show in Island
    const showInIsland = true;

    notification = {
        id: Date.now().toString(),
        message,
        description,
        type,
        isVisible: showInIsland,
        link,
        action: action ?? null,
        actionLabel,
    };
    emitChange();

    if (showInIsland) {
        dismissTimer = setTimeout(() => dismiss(), duration);
    }
}

export function dismiss(): void {
    if (dismissTimer) {
        clearTimeout(dismissTimer);
        dismissTimer = null;
    }
    notification = { ...notification, isVisible: false };
    emitChange();
}

export function getNotificationSnapshot(): Notification {
    return notification;
}

export function subscribeToNotifications(callback: () => void): () => void {
    listeners.add(callback);
    return () => { listeners.delete(callback); };
}

export function useNotificationStore(): Notification {
    return useSyncExternalStore(subscribeToNotifications, getNotificationSnapshot);
}

export const toast = {
    success: (message: string, opts?: ToastOptions) => notify(message, 'success', opts?.description, opts?.duration, opts?.link, opts?.action, opts?.actionLabel),
    error: (message: string, opts?: ToastOptions) => notify(message, 'error', opts?.description, opts?.duration ?? 4000, opts?.link, opts?.action, opts?.actionLabel),
    warning: (message: string, opts?: ToastOptions) => notify(message, 'warning', opts?.description, opts?.duration, opts?.link, opts?.action, opts?.actionLabel),
    info: (message: string, opts?: ToastOptions) => notify(message, 'info', opts?.description, opts?.duration, opts?.link, opts?.action, opts?.actionLabel),
    library: (message: string, opts?: ToastOptions) => notify(message, 'library', opts?.description, opts?.duration, opts?.link, opts?.action, opts?.actionLabel),
    sparkles: (message: string, opts?: ToastOptions) => notify(message, 'sparkles', opts?.description, opts?.duration, opts?.link, opts?.action, opts?.actionLabel),
    message: (message: string, opts?: ToastOptions) => notify(message, 'default', opts?.description, opts?.duration, opts?.link, opts?.action, opts?.actionLabel),
};
